import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Calculator, Download, Calendar, TrendingUp, TrendingDown,
  Info, AlertCircle, Loader2, ChevronDown, ChevronUp,
  ArrowRight, DollarSign, FileText, Scale, BarChart2,
  Clock, Tag, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur, getTaxLabels } from '@/lib/market';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(v, market) {
  if (v === null || v === undefined) return '—';
  return fmtCur(v, market);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function holdingLabel(days) {
  if (!days && days !== 0) return '—';
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
}

// US: LTCG = held > 365d; India equity/ETF/MF: LTCG = held > 365d; debt: > 730d
function classifyGain(gainType, holdingDays, assetClass, market) {
  if (gainType) return gainType.toUpperCase(); // use what backend says
  const ltcgDays = (market === 'IN' && ['bonds', 'fd'].includes(assetClass)) ? 730 : 365;
  return holdingDays >= ltcgDays ? 'LTCG' : 'STCG';
}

// Days between two YYYY-MM-DD strings
function daysBetween(buyDate, sellDate) {
  if (!buyDate || !sellDate) return null;
  try {
    const b = new Date(buyDate.slice(0, 10));
    const s = new Date(sellDate.slice(0, 10));
    return Math.max(0, Math.round((s - b) / 86400000));
  } catch { return null; }
}

function mapTransaction(cg, market) {
  const gainType = cg.gain_type || classifyGain(null, cg.holding_period_days, cg.asset_class, market);
  const days = cg.holding_period_days ?? daysBetween(cg.buy_date, cg.sell_date);
  const isLoss = (cg.gain || 0) < 0;
  return {
    id: cg.id,
    symbol: cg.symbol || cg.name || '—',
    name: cg.name || cg.symbol || '—',
    asset: (cg.asset_class || '').replace('_', ' '),
    assetRaw: cg.asset_class || '',
    gainType,
    isLTCG: gainType === 'LTCG',
    isSTCG: gainType === 'STCG',
    isLoss,
    buyDate: cg.buy_date ? cg.buy_date.slice(0, 10) : null,
    sellDate: cg.sell_date ? cg.sell_date.slice(0, 10) : null,
    buyPrice: cg.buy_price || 0,
    sellPrice: cg.sell_price || 0,
    qty: cg.quantity || 0,
    buyValue: cg.buy_value || ((cg.buy_price || 0) * (cg.quantity || 0)),
    sellValue: cg.sell_value || ((cg.sell_price || 0) * (cg.quantity || 0)),
    gain: cg.gain || 0,
    taxableGain: cg.taxable_gain ?? cg.gain ?? 0,
    holdingDays: days,
    holdingLabel: holdingLabel(days),
    grandfathered: cg.is_grandfathered || false,
    computed: cg.computed || false,
  };
}

function buildSummary(data, bd) {
  return {
    stcg: data.stcg || 0,
    ltcg: data.ltcg || 0,
    stcgTax: data.stcg_tax || 0,
    ltcgTax: data.ltcg_tax || 0,
    ltcgExemption: data.ltcg_exemption || 0,
    ltcgTaxable: data.ltcg_taxable || 0,
    totalTax: data.total_tax || 0,
    intraday: data.intraday || 0,
    fno: data.fno || 0,
    byAsset: bd || {},
  };
}

// ── Small components ───────────────────────────────────────────────────────────

function GainTypeBadge({ type }) {
  const cfg = {
    STCG:    'bg-[#c97a3a]/10 text-[#c97a3a] border-[#c97a3a]/30',
    LTCG:    'bg-amber-50 text-amber-700 border-amber-200',
    Intraday:'bg-[#f9f9f9] text-[#4a4a4a] border-[#e5e5e5]',
    'F&O':   'bg-[#f9f9f9] text-[#4a4a4a] border-[#e5e5e5]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border ${cfg[type] || 'bg-[#f9f9f9] text-[#4a4a4a] border-[#e5e5e5]'}`}>
      {type}
    </span>
  );
}

function SummaryCard({ icon: Icon, iconColor, label, value, sub, subColor, badge }) {
  return (
    <div className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-5 h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-[#ffd87a]" />
          </div>
          <span className="text-xs text-[#4a4a4a] uppercase tracking-widest font-medium">{label}</span>
        </div>
        {badge && (
          <span className="text-xs font-semibold px-2 py-0.5 bg-[#f5f5f5] text-[#4a4a4a] rounded">{badge}</span>
        )}
      </div>
      <div className={`text-2xl font-bold mb-1.5 ${(value || 0) < 0 ? 'text-[#c97a3a]' : 'text-[#1a1a1a]'}`}>
        {value}
      </div>
      {sub && <div className={`text-xs font-medium ${subColor || 'text-[#4a4a4a]'}`}>{sub}</div>}
    </div>
  );
}

function HoldingsGainTable({ holdings, market, isUS }) {
  const now = new Date();
  if (!holdings || !holdings.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#dae4c4]">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Symbol</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Purchase Date</th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Avg Cost</th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Qty</th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Unrealized P&L</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Holding</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Tax Class</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#e5e5e5]">
          {holdings.map((h, i) => {
            const days = h.purchase_date ? daysBetween(h.purchase_date, now.toISOString().slice(0, 10)) : null;
            const ltcgDays = (market === 'IN' && ['bonds', 'fd'].includes(h.asset_class)) ? 730 : 365;
            const taxClass = days !== null ? (days >= ltcgDays ? 'LTCG' : 'STCG') : '—';
            const daysToLTCG = days !== null && days < ltcgDays ? ltcgDays - days : 0;
            const unrealized = ((h.current_price || 0) - (h.avg_price || h.avg_buy_price || 0)) * (h.quantity || 0);
            return (
              <tr key={h.id || i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#1a1a1a]">{h.symbol}</div>
                  <div className="text-xs text-[#4a4a4a] capitalize">{(h.asset_class || '').replace('_', ' ')}</div>
                </td>
                <td className="px-4 py-3 text-[#4a4a4a]">{h.purchase_date ? fmtDate(h.purchase_date) : '—'}</td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{fmt(h.avg_price || h.avg_buy_price, market)}</td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{h.quantity}</td>
                <td className={`px-4 py-3 text-right font-semibold ${unrealized >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                  {unrealized >= 0 ? '+' : ''}{fmt(unrealized, market)}
                </td>
                <td className="px-4 py-3">
                  <div className="text-[#2a2a2a]">{days !== null ? holdingLabel(days) : '—'}</div>
                  {daysToLTCG > 0 && (
                    <div className="text-xs text-amber-500 mt-0.5">{holdingLabel(daysToLTCG)} to LTCG</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {taxClass !== '—' ? <GainTypeBadge type={taxClass} /> : <span className="text-[#4a4a4a] text-xs">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function CapitalGainsPage() {
  const { user } = useAuth();
  const { financialYears: contextFY } = useAppData();
  const market  = user?.market || 'IN';
  const isUS    = market === 'US';
  const taxLbls = getTaxLabels(market);
  const fc      = (v) => fmt(v, market);

  // Default FY: US = calendar year, India = Apr-Mar
  const [selectedYear, setSelectedYear] = useState(() => {
    const now = new Date();
    if (isUS) return String(now.getFullYear());
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(2)}`;
  });
  const [selectedAsset, setSelectedAsset] = useState('all');
  const [selectedType,  setSelectedType]  = useState('all');
  const [showHoldings,  setShowHoldings]  = useState(false);

  const [summary,     setSummary]     = useState(null);
  const [transactions,setTransactions]= useState([]);
  const [holdings,    setHoldings]    = useState([]);
  const [loadingSummary, setLoadingS] = useState(false);
  const [loadingTxns,    setLoadingT] = useState(false);
  const [exportingPDF,   setExportingPDF] = useState(false);

  const fyList = contextFY.length > 0
    ? contextFY.map(f => f.key)
    : isUS
      ? ['2026','2025','2024','2023','2022']
      : ['2025-26','2024-25','2023-24','2022-23','2021-22'];

  const fetchSummary = useCallback(async () => {
    setLoadingS(true);
    try {
      const r = await axios.get(`${API}/markets/capital-gains/summary`, {
        params: { financial_year: selectedYear },
      });
      setSummary(buildSummary(r.data, r.data.breakdown_by_asset));
    } catch { setSummary(null); }
    finally { setLoadingS(false); }
  }, [selectedYear]);

  const fetchTransactions = useCallback(async () => {
    setLoadingT(true);
    try {
      const p = { financial_year: selectedYear };
      if (selectedAsset !== 'all') p.asset_class = selectedAsset;
      if (selectedType !== 'all') p.gain_type = selectedType;
      const r = await axios.get(`${API}/markets/capital-gains/transactions`, { params: p });
      setTransactions(r.data.map(t => mapTransaction(t, market)));
    } catch { setTransactions([]); }
    finally { setLoadingT(false); }
  }, [selectedYear, selectedAsset, selectedType, market]);

  const fetchHoldings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/markets/holdings`);
      setHoldings(r.data || []);
    } catch { setHoldings([]); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchHoldings(); }, [fetchHoldings]);

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const res = await axios.get(`${API}/markets/capital-gains/export/pdf`, {
        params: { financial_year: selectedYear },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isUS ? `form_8949_${selectedYear}.pdf` : `schedule_cg_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Export failed';
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExportingPDF(false);
    }
  };

  const s = summary || {
    stcg: 0, ltcg: 0, stcgTax: 0, ltcgTax: 0,
    ltcgExemption: 0, ltcgTaxable: 0, totalTax: 0,
    intraday: 0, fno: 0, byAsset: {},
  };

  const netGain   = s.stcg + s.ltcg + s.intraday + s.fno;
  const hasData   = netGain !== 0 || transactions.length > 0;

  // Separate losses for harvesting section
  const lossRows = transactions.filter(t => t.gain < 0);
  const gainRows = transactions.filter(t => t.gain >= 0);

  return (
    <div className="p-6 space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <Calculator className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Capital Gains</h1>
            <p className="text-xs text-[#4a4a4a] mt-0.5">
              {isUS ? 'Schedule D / Form 8949' : 'ITR Schedule CG'} ·{' '}
              {isUS ? `Tax Year ${selectedYear}` : `FY ${selectedYear}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-36 bg-white border-[#e5e5e5] text-[#1a1a1a] text-sm">
              <Calendar className="w-3.5 h-3.5 mr-1.5 text-[#4a4a4a]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e5e5e5]">
              {fyList.map(y => (
                <SelectItem key={y} value={y} className="text-[#1a1a1a] text-sm hover:bg-[#f9f9f9]">
                  {isUS ? `Tax Year ${y}` : `FY ${y}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm"
            className="border-[#e5e5e5] text-[#4a4a4a] hover:bg-[#f5f5f5] hover:text-[#1a1a1a]"
            onClick={handleExportPDF}
            disabled={exportingPDF}
          >
            {exportingPDF
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Download className="w-3.5 h-3.5 mr-1.5" />}
            {isUS ? 'Export 8949' : 'Export ITR'}
          </Button>
        </div>
      </div>

      {loadingSummary ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-[#ffd87a]" />
        </div>
      ) : (
        <>
          {/* ── Summary tiles ── */}
          <div className={`grid gap-4 ${isUS ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-4'}`}>
            <SummaryCard
              icon={TrendingUp}
              iconColor="bg-[#c97a3a]/15 text-[#c97a3a]"
              label={isUS ? 'Short-Term Gains' : 'STCG'}
              value={fc(s.stcg)}
              badge={isUS ? '< 1yr' : '< 1yr'}
              sub={`Tax @ ${taxLbls.shortRate} = ${fc(s.stcgTax)}`}
              subColor="text-[#c97a3a]"
            />
            <SummaryCard
              icon={CheckCircle2}
              iconColor="bg-[#ffd879]/15 text-[#ffd879]"
              label={isUS ? 'Long-Term Gains' : 'LTCG'}
              value={fc(s.ltcg)}
              badge={isUS ? '> 1yr' : '> 1yr'}
              sub={
                !isUS && s.ltcgExemption > 0
                  ? `Exempt: ${fc(s.ltcgExemption)} · Tax ${fc(s.ltcgTax)}`
                  : `Tax @ ${taxLbls.longRate} = ${fc(s.ltcgTax)}`
              }
              subColor="text-[#ffd879]"
            />
            {!isUS && (
              <SummaryCard
                icon={BarChart2}
                iconColor="bg-[#d9e2c3]/20 text-[#d9e2c3]"
                label="F&O / Intraday"
                value={fc(s.intraday + s.fno)}
                sub="Business income · Slab rate"
                subColor="text-[#4a4a4a]"
              />
            )}
            {isUS && (
              <SummaryCard
                icon={DollarSign}
                iconColor="bg-[#2a2a2a] text-[#4a4a4a]"
                label="NIIT Exposure"
                value={fc((s.ltcg + s.stcg) * 0.038)}
                badge="3.8%"
                sub="Net Investment Income Tax (if AGI > $200K)"
                subColor="text-[#4a4a4a]"
              />
            )}
            <SummaryCard
              icon={FileText}
              iconColor="bg-[#c97a3a]/15 text-[#c97a3a]"
              label="Total Tax Liability"
              value={fc(s.totalTax)}
              sub={isUS
                ? `STCG: ${fc(s.stcgTax)} + LTCG: ${fc(s.ltcgTax)} · Federal only`
                : `STCG: ${fc(s.stcgTax)} + LTCG: ${fc(s.ltcgTax)} · Excl. cess`}
              subColor="text-[#c97a3a]"
            />
          </div>

          {/* ── Tax calculation breakdown ── */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <Scale className="w-4 h-4 text-[#4a4a4a]" />
              <h3 className="text-sm font-semibold text-[#2a2a2a]">Tax Calculation Breakdown</h3>
              <span className="text-xs text-[#4a4a4a] ml-auto">{isUS ? 'Federal rates' : 'FY rules'}</span>
            </div>

            {isUS ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a]">
                      Short-Term ({taxLbls.short})
                      <ArrowRight className="w-3 h-3 text-[#4a4a4a]" />
                      <span className="text-amber-600">Ordinary income rate ≈ {taxLbls.shortRate}</span>
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      Assets held ≤ 12 months. Taxed as regular income (up to 37% federal + FICA).
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-[#1a1a1a]">{fc(s.stcg)}</div>
                    <div className="text-xs text-amber-600">Tax: {fc(s.stcgTax)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a]">
                      Long-Term ({taxLbls.long})
                      <ArrowRight className="w-3 h-3 text-[#4a4a4a]" />
                      <span className="text-emerald-600">Preferential rate: 0% / 15% / {taxLbls.longRate}</span>
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      Assets held &gt; 12 months. Rate depends on taxable income bracket. Estimated at top 20% rate here.
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-[#1a1a1a]">{fc(s.ltcg)}</div>
                    <div className="text-xs text-emerald-600">Tax: {fc(s.ltcgTax)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="w-2 h-2 rounded-full bg-purple-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1a1a1a]">
                      Net Investment Income Tax (NIIT) — 3.8%
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      Applies if MAGI exceeds $200K (single) / $250K (MFJ). Includes interest, dividends, capital gains.
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-[#1a1a1a]">{fc((s.stcg + s.ltcg) * 0.038)}</div>
                    <div className="text-xs text-purple-600">Estimated exposure</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-[#f9f9f9] rounded-lg border border-[#e5e5e5] text-xs text-[#4a4a4a]">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#4a4a4a]" />
                  <span>
                    <strong className="text-[#2a2a2a]">Wash-Sale Rule:</strong> If you sold at a loss and repurchased the same
                    security within 30 days before or after, the loss is disallowed and added to the cost basis of the repurchased shares.
                    Loss harvesting requires waiting 31+ days before repurchase.
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a]">
                      STCG (Section 111A)
                      <ArrowRight className="w-3 h-3 text-[#4a4a4a]" />
                      <span className="text-amber-600">{taxLbls.shortRate} flat</span>
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      Equity / equity MF / ETF held ≤ 12 months. Debt held ≤ 24 months at slab rate.
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-[#1a1a1a]">{fc(s.stcg)}</div>
                    <div className="text-xs text-amber-600">Tax: {fc(s.stcgTax)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#1a1a1a]">
                      LTCG (Section 112A)
                      <ArrowRight className="w-3 h-3 text-[#4a4a4a]" />
                      <span className="text-emerald-600">{taxLbls.longRate} above ₹1.25L exemption</span>
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      Equity / equity MF held &gt; 12 months. Grandfathering applies for shares bought before Jan 31, 2018 (FMV as cost).
                      Debt LTCG at slab rate (no indexation from FY25).
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-[#1a1a1a]">{fc(s.ltcg)}</div>
                    <div className="text-xs text-[#4a4a4a]">Exempt: {fc(s.ltcgExemption)}</div>
                    <div className="text-xs text-emerald-600">Tax: {fc(s.ltcgTax)}</div>
                  </div>
                </div>

                {(s.intraday !== 0 || s.fno !== 0) && (
                  <div className="flex items-center gap-3 p-3 bg-[#f9f9f9] rounded-lg border border-[#e5e5e5]">
                    <div className="w-2 h-2 rounded-full bg-[#a8a8a8] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1a1a1a]">F&O &amp; Intraday — Business Income</div>
                      <div className="text-xs text-[#4a4a4a] mt-0.5">
                        Not capital gains. Taxed at slab rate. Eligible for set-off against business expenses.
                        Requires tax audit if turnover &gt; ₹1 Cr (or ₹10 Cr for digital transactions).
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-base font-bold text-[#1a1a1a]">{fc(s.intraday + s.fno)}</div>
                      <div className="text-xs text-[#4a4a4a]">At slab rate</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Asset class breakup ── */}
          {Object.keys(s.byAsset).length > 0 && (
            <div className="bg-white border border-[#e5e5e5] rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-4 h-4 text-[#4a4a4a]" />
                <h3 className="text-sm font-semibold text-[#2a2a2a]">By Asset Class</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(s.byAsset).map(([ac, vals]) => {
                  const total = (vals.stcg || 0) + (vals.ltcg || 0) + (vals.intraday || 0) + (vals.fno || 0);
                  return (
                    <div key={ac} className="bg-[#f9f9f9] rounded-lg p-3 border border-[#e5e5e5]">
                      <div className="text-xs text-[#4a4a4a] capitalize mb-1">{ac.replace('_', ' ')}</div>
                      <div className={`text-base font-bold ${total < 0 ? 'text-[#c97a3a]' : 'text-[#1a1a1a]'}`}>{fc(total)}</div>
                      <div className="text-xs text-[#4a4a4a] mt-1 space-y-0.5">
                        {vals.stcg !== 0 && <div>STCG: {fc(vals.stcg)}</div>}
                        {vals.ltcg !== 0 && <div>LTCG: {fc(vals.ltcg)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Loss harvesting ── */}
          {lossRows.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-700">Loss Harvest Positions</h3>
                <span className="ml-auto text-xs text-amber-600">
                  Net loss: {fc(lossRows.reduce((a, t) => a + t.gain, 0))}
                </span>
              </div>
              <div className="text-xs text-amber-700 mb-3">
                {isUS
                  ? 'These losses can offset capital gains dollar-for-dollar. Up to $3,000 of excess losses can offset ordinary income annually. Remainder carries forward.'
                  : 'STCG losses can offset STCG and LTCG. LTCG losses can only offset LTCG. Unabsorbed losses carry forward for 8 years.'}
              </div>
              <div className="flex flex-wrap gap-2">
                {lossRows.map((t, i) => (
                  <div key={t.id || i} className="bg-white border border-amber-200 rounded-lg px-3 py-2 text-xs">
                    <span className="font-semibold text-[#1a1a1a]">{t.symbol}</span>
                    <span className="ml-2 text-[#c97a3a] font-medium">{fc(t.gain)}</span>
                    <span className="ml-1 text-[#4a4a4a]">({t.gainType})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Filters ── */}
      <div className="flex items-center gap-3">
        <Select value={selectedAsset} onValueChange={setSelectedAsset}>
          <SelectTrigger className="w-40 bg-white border-[#e5e5e5] text-[#1a1a1a] text-sm">
            <SelectValue placeholder="All Assets" />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#e5e5e5]">
            <SelectItem value="all" className="text-[#1a1a1a] text-sm">All Assets</SelectItem>
            <SelectItem value="stocks" className="text-[#1a1a1a] text-sm">Stocks</SelectItem>
            {isUS
              ? <SelectItem value="etf" className="text-[#1a1a1a] text-sm">ETFs</SelectItem>
              : <>
                  <SelectItem value="mf" className="text-[#1a1a1a] text-sm">Mutual Funds</SelectItem>
                  <SelectItem value="fo" className="text-[#1a1a1a] text-sm">F&O</SelectItem>
                </>
            }
            <SelectItem value="bonds" className="text-[#1a1a1a] text-sm">Bonds</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-40 bg-white border-[#e5e5e5] text-[#1a1a1a] text-sm">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#e5e5e5]">
            <SelectItem value="all" className="text-[#1a1a1a] text-sm">All Types</SelectItem>
            <SelectItem value="STCG" className="text-[#1a1a1a] text-sm">{isUS ? 'Short-Term' : 'STCG'}</SelectItem>
            <SelectItem value="LTCG" className="text-[#1a1a1a] text-sm">{isUS ? 'Long-Term' : 'LTCG'}</SelectItem>
            {!isUS && <>
              <SelectItem value="Intraday" className="text-[#1a1a1a] text-sm">Intraday</SelectItem>
              <SelectItem value="F&O" className="text-[#1a1a1a] text-sm">F&O</SelectItem>
            </>}
          </SelectContent>
        </Select>
        {loadingTxns && <Loader2 className="w-4 h-4 animate-spin text-[#ffd879]" />}
      </div>

      {/* ── Transactions table ── */}
      <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden shadow-sm">
        <div className="px-5 py-3.5 border-b border-[#e5e5e5] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#4a4a4a]" />
          <h3 className="text-sm font-semibold text-[#2a2a2a]">
            {isUS ? 'Form 8949 — Realized Transactions' : 'Schedule CG — Realized Transactions'}
          </h3>
          <span className="ml-auto text-xs text-[#4a4a4a]">{transactions.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#dae4c4]">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Asset</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Buy Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Sell Date</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Holding</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Qty</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Buy Price</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Sell Price</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Cost Basis</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Proceeds</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#1a1a1a]">Gain / Loss</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e5e5]">
              {transactions.length === 0 && !loadingTxns ? (
                <tr>
                  <td colSpan={11} className="px-4 py-14 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Calculator className="w-7 h-7 text-[#4a4a4a]" />
                      <p className="text-sm text-[#4a4a4a] font-medium">
                        No realized transactions for {isUS ? 'Tax Year' : 'FY'} {selectedYear}
                      </p>
                      <p className="text-xs text-[#4a4a4a]">Capital gains are calculated automatically when sell transactions are recorded</p>
                    </div>
                  </td>
                </tr>
              ) : (
                transactions.map((t, i) => (
                  <tr key={t.id || i} className={`hover:bg-[#f9f9f9] transition-colors ${t.isLoss ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#1a1a1a]">{t.symbol}</div>
                      <div className="text-xs text-[#4a4a4a] capitalize">{t.asset}</div>
                      {t.grandfathered && (
                        <span className="text-xs text-amber-600 font-medium">Grandfathered</span>
                      )}
                      {t.computed && (
                        <span className="text-xs text-[#4a4a4a]">Auto-computed</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><GainTypeBadge type={t.gainType} /></td>
                    <td className="px-4 py-3 text-[#4a4a4a]">{t.buyDate ? fmtDate(t.buyDate) : '—'}</td>
                    <td className="px-4 py-3 text-[#4a4a4a]">{t.sellDate ? fmtDate(t.sellDate) : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-[#4a4a4a]">
                        <Clock className="w-3 h-3" />{t.holdingLabel}
                      </div>
                      <div className="text-xs text-[#4a4a4a] mt-0.5">
                        {t.holdingDays !== null ? `${t.holdingDays} days` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-[#4a4a4a]">{t.qty}</td>
                    <td className="px-4 py-3 text-right text-[#4a4a4a]">{fc(t.buyPrice)}</td>
                    <td className="px-4 py-3 text-right text-[#4a4a4a]">{fc(t.sellPrice)}</td>
                    <td className="px-4 py-3 text-right text-[#4a4a4a]">{fc(t.buyValue)}</td>
                    <td className="px-4 py-3 text-right text-[#4a4a4a]">{fc(t.sellValue)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${t.isLoss ? 'text-[#c97a3a]' : 'text-emerald-600'}`}>
                      {t.isLoss ? '' : '+'}{fc(t.gain)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {transactions.length > 0 && (
              <tfoot>
                <tr className="bg-[#f9f9f9] border-t-2 border-[#e5e5e5]">
                  <td colSpan={8} className="px-4 py-3 text-xs font-semibold text-[#4a4a4a]">Net Total</td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-[#2a2a2a]">
                    {fc(transactions.reduce((a, t) => a + t.buyValue, 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-[#2a2a2a]">
                    {fc(transactions.reduce((a, t) => a + t.sellValue, 0))}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-bold ${netGain < 0 ? 'text-[#c97a3a]' : 'text-emerald-600'}`}>
                    {netGain < 0 ? '' : '+'}{fc(transactions.reduce((a, t) => a + t.gain, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Unrealized positions ── */}
      <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden shadow-sm">
        <button
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-[#f9f9f9] transition-colors"
          onClick={() => setShowHoldings(v => !v)}
        >
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-[#4a4a4a]" />
            <span className="text-sm font-semibold text-[#2a2a2a]">Unrealized Positions — Tax Classification</span>
            <span className="text-xs text-[#4a4a4a]">({holdings.length} holdings)</span>
          </div>
          {showHoldings ? <ChevronUp className="w-4 h-4 text-[#4a4a4a]" /> : <ChevronDown className="w-4 h-4 text-[#4a4a4a]" />}
        </button>
        {showHoldings && (
          <div className="border-t border-[#e5e5e5]">
            <HoldingsGainTable holdings={holdings} market={market} isUS={isUS} />
          </div>
        )}
      </div>

      {/* ── Disclaimer ── */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-700 space-y-1">
          <p className="font-semibold">Important — Estimates Only</p>
          {isUS ? (
            <ul className="list-disc list-inside space-y-0.5 text-amber-600">
              <li>STCG rate assumed at 37% (top bracket). Your actual rate depends on total taxable income.</li>
              <li>LTCG rates: 0% (income ≤ $47K), 15% ($47K–$518K), 20% (above). Estimated at 20%.</li>
              <li>NIIT (3.8%) applies only if MAGI exceeds $200K (single) / $250K (married). State taxes not included.</li>
              <li>Cost basis uses FIFO. Consult a CPA for specific lot identification (SpecID) or average cost methods.</li>
            </ul>
          ) : (
            <ul className="list-disc list-inside space-y-0.5 text-amber-600">
              <li>LTCG on equity @ 12.5% above ₹1.25 L per year (Budget 2024). Grandfathering applies for pre-Jan 31, 2018 shares.</li>
              <li>STCG on equity @ 20% flat. Debt LTCG / STCG at income slab rate (no indexation from FY 2024-25).</li>
              <li>F&O / Intraday treated as non-speculative / speculative business income respectively.</li>
              <li>Add 4% health & education cess on total tax. Surcharge applies above ₹50 L income.</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
